import { JOVE_UNIVERSAL_AUTH_SESSION_KEY } from '../constants';
var SSO_COOKIE_NAME = 'jove-sso-session';
/** Keep every cookie well under the 4KB per-cookie browser limit; JWT pairs can exceed it, so the payload is chunked */

var SSO_COOKIE_CHUNK_SIZE = 3000;
var SSO_COOKIE_MAX_CHUNKS = 8;
/** Decodes a JWT payload without verification; the token was issued by our own backend over TLS */

var decodeIdTokenClaims = function decodeIdTokenClaims(idToken) {
  try {
    var base64Url = idToken.split('.')[1];
    if (!base64Url) return undefined; // base64url → base64, then UTF-8 safe decode (claims may contain non-ASCII names)

    var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    var bytes = Uint8Array.from(window.atob(base64), function (_char) {
      return _char.charCodeAt(0);
    });
    return JSON.parse(new TextDecoder().decode(bytes));
  } catch (_) {
    return undefined;
  }
};
/** Hostname of the backend this app talks to (NX_JOVE_API), e.g. `api.jove.co` / `api.staging.jove.co` */


var resolveApiHost = function resolveApiHost() {
  try {
    return new URL(process.env['NX_JOVE_API'] || '').hostname || undefined;
  } catch (_) {
    return undefined;
  }
};
/**
 * Each API environment gets its own cookie slot (e.g. `jove-sso-session-api-staging-jove-co`),
 * so sessions from different backend tenants never hydrate into each other on shared domains
 * (localhost is one shared cookie jar across ports; `.jove.co` is shared across subdomains).
 */


var resolveSsoCookieName = function resolveSsoCookieName() {
  var apiHost = resolveApiHost();
  var envKey = apiHost ? apiHost.replace(/[^a-z0-9]/gi, '-').toLowerCase() : 'default';
  return "".concat(SSO_COOKIE_NAME, "-").concat(envKey);
};
/**
 * Resolves the cookie domain shared by all Jove apps in the current environment.
 * The website host (NX_WEBSITE_URL) is always the root-most domain of an environment
 * (jove.co in production, staging.jove.co in staging), which scopes the cookie so staging
 * sessions never leak into production. localhost/IPs fall back to a host-only cookie,
 * which is still shared across local ports.
 */


var resolveSsoCookieDomain = function resolveSsoCookieDomain() {
  var hostname = window.location.hostname;
  if (hostname === 'localhost' || /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname)) return undefined;

  try {
    var websiteHostname = new URL(process.env['NX_WEBSITE_URL'] || '').hostname;

    if (websiteHostname && (hostname === websiteHostname || hostname.endsWith(".".concat(websiteHostname)))) {
      return ".".concat(websiteHostname);
    }
  } catch (_) {}

  var labels = hostname.split('.');
  return labels.length > 1 ? ".".concat(labels.slice(-2).join('.')) : undefined;
};

var buildSsoCookieAttributes = function buildSsoCookieAttributes(maxAgeSeconds) {
  var attributes = ['Path=/', 'SameSite=Lax', "Max-Age=".concat(Math.max(0, Math.floor(maxAgeSeconds)))];
  var domain = resolveSsoCookieDomain();
  if (domain) attributes.push("Domain=".concat(domain));
  if (window.location.protocol === 'https:') attributes.push('Secure');
  return attributes.join('; ');
};

var readCookie = function readCookie(name) {
  var match = document.cookie.match(new RegExp("(?:^|; )".concat(name.replace(/\./g, '\\.'), "=([^;]*)")));
  return match ? decodeURIComponent(match[1]) : null;
};

var writeSsoCookie = function writeSsoCookie(value, maxAgeSeconds) {
  var cookieName = resolveSsoCookieName();
  var chunks = [];

  for (var start = 0; start < value.length; start += SSO_COOKIE_CHUNK_SIZE) {
    chunks.push(value.slice(start, start + SSO_COOKIE_CHUNK_SIZE));
  }

  var attributes = buildSsoCookieAttributes(maxAgeSeconds);
  document.cookie = "".concat(cookieName, ".chunks=").concat(chunks.length, "; ").concat(attributes);
  chunks.forEach(function (chunk, index) {
    document.cookie = "".concat(cookieName, ".").concat(index, "=").concat(encodeURIComponent(chunk), "; ").concat(attributes);
  }); // expire stale chunks left by a previously longer session

  var clearAttributes = buildSsoCookieAttributes(0);

  for (var index = chunks.length; index < SSO_COOKIE_MAX_CHUNKS; index++) {
    document.cookie = "".concat(cookieName, ".").concat(index, "=; ").concat(clearAttributes);
  }
};

var readSsoCookie = function readSsoCookie() {
  var cookieName = resolveSsoCookieName();
  var chunkCount = Number(readCookie("".concat(cookieName, ".chunks")) || 0);
  if (!chunkCount) return null;
  var value = '';

  for (var index = 0; index < chunkCount; index++) {
    var chunk = readCookie("".concat(cookieName, ".").concat(index));
    if (chunk === null) return null;
    value += chunk;
  }

  return value;
};
/** Exported for the cross-app logout sync: deleting the shared cookie from any subdomain signs out every app of the same API environment */


export var clearJoveAuthSsoCookie = function clearJoveAuthSsoCookie() {
  if (false) return;
  var cookieName = resolveSsoCookieName();
  var attributes = buildSsoCookieAttributes(0);
  document.cookie = "".concat(cookieName, ".chunks=; ").concat(attributes);

  for (var index = 0; index < SSO_COOKIE_MAX_CHUNKS; index++) {
    document.cookie = "".concat(cookieName, ".").concat(index, "=; ").concat(attributes);
  }
};

var parseSession = function parseSession(raw) {
  if (!raw) return null;

  try {
    var session = JSON.parse(raw);
    if (!(session !== null && session !== void 0 && session.accessToken) || !(session !== null && session !== void 0 && session.idToken) || !(session !== null && session !== void 0 && session.expiresAt)) return null;
    return session;
  } catch (_) {
    return null;
  }
};
/**
 * A session is only usable by the backend tenant that issued it. Sessions without an apiHost
 * stamp predate the per-environment cookie split and are treated as invalid (forces one re-login).
 */


var isSessionForCurrentApi = function isSessionForCurrentApi(session) {
  var currentApiHost = resolveApiHost();
  if (!currentApiHost) return true;
  return session.apiHost === currentApiHost;
};

export var saveJoveAuthSession = function saveJoveAuthSession(data) {
  var _data$refreshToken;

  if (false || !(data !== null && data !== void 0 && data.accessToken) || !(data !== null && data !== void 0 && data.idToken)) return null;
  var claims = decodeIdTokenClaims(data.idToken);
  var session = {
    accessToken: data.accessToken,
    idToken: data.idToken,
    tokenType: data.tokenType,
    expiresIn: data.expiresIn,
    expiresAt: claims !== null && claims !== void 0 && claims['exp'] ? claims['exp'] * 1000 : Date.now() + (data.expiresIn || 0) * 1000,
    refreshToken: (_data$refreshToken = data.refreshToken) !== null && _data$refreshToken !== void 0 ? _data$refreshToken : null,
    scope: data.scope,
    roles: data.roles || [],
    user: claims,
    apiHost: resolveApiHost()
  };
  var serialized = JSON.stringify(session);
  window.localStorage.setItem(JOVE_UNIVERSAL_AUTH_SESSION_KEY, serialized);
  writeSsoCookie(serialized, (session.expiresAt - Date.now()) / 1000);
  return session;
};
export var loadJoveAuthSession = function loadJoveAuthSession() {
  if (false) return null;
  var localSession = parseSession(window.localStorage.getItem(JOVE_UNIVERSAL_AUTH_SESSION_KEY));

  if (localSession && localSession.expiresAt > Date.now() && isSessionForCurrentApi(localSession)) {
    return localSession;
  } // No valid local copy: hydrate from the cross-app SSO cookie (the user logged in on a sibling app)


  var ssoSession = parseSession(readSsoCookie());

  if (ssoSession && ssoSession.expiresAt > Date.now() && isSessionForCurrentApi(ssoSession)) {
    window.localStorage.setItem(JOVE_UNIVERSAL_AUTH_SESSION_KEY, JSON.stringify(ssoSession));
    return ssoSession;
  }

  if (localSession || ssoSession) {
    clearJoveAuthSession();
  }

  return null;
};
export var clearJoveAuthSession = function clearJoveAuthSession() {
  if (false) return;
  window.localStorage.removeItem(JOVE_UNIVERSAL_AUTH_SESSION_KEY);
  clearJoveAuthSsoCookie();
};